import {
	Button, Divider, Form,
} from 'antd-mobile';
import { Input, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface MQFormValues {
	[key: number]: string
}

interface AttemptMQProps {
	questionData: AttemptQuestionData
	submitting: boolean
	disableCopyPaste?: boolean
	onSubmit: (data: any) => Promise<void>
	setAttempt: (attemptData: unknown) => void
	getAttempt: () => any | null
	sendPasteNotification: () => void,
}

export const AttemptMQ: React.FunctionComponent<AttemptMQProps> = (props) => {
	const {
		questionData, submitting: isSubmitting, disableCopyPaste, onSubmit, setAttempt, getAttempt,
		sendPasteNotification,
	} = props;
	const [form] = Form.useForm();

	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

	useEffect(() => {
		setPortalTarget(document.getElementById('mobile-submit-portal'));
	}, []);

	const [token, setToken] = useState<string | null>(null);
	const [stringCopied, setStringCopied] = useState<string>('');

	const saveAttempt = useCallback(() => {
		setAttempt({
			values: Object.values(form.getFieldsValue()),
		});
	}, [form, setAttempt]);

	const handleCopy = useCallback(async (ev: React.ClipboardEvent<HTMLTextAreaElement>) => {
		try {
			const newToken = crypto.randomUUID();
			await navigator.clipboard.writeText(newToken);
			if (ev.target) {
				const element = (ev.target as HTMLTextAreaElement);
				const start = element.selectionStart;
				const end = element.selectionEnd;
				const content = element.value;
				const contentToCopy = content.slice(start, end);
				setStringCopied(contentToCopy);
			}
			setToken(newToken);
		} catch (error) {
			console.log(error);
		}
	}, [setToken, setStringCopied]);

	const handlePaste = useCallback(async (ev: React.ClipboardEvent<HTMLTextAreaElement>) => {
		try {
			const data = await navigator.clipboard.readText();
			if (!data) {
				return;
			}
			if (disableCopyPaste) {
				if (data !== token) {
					message.error('Attention: Please do not use copy paste as it may result in disqualification.');
					sendPasteNotification();
					return;
				}
			}
			if (ev.target) {
				const element = (ev.target as HTMLTextAreaElement);
				const start = element.selectionStart;
				const end = element.selectionEnd;
				const content = element.value;
				const newContent = content.slice(0, start) + stringCopied + content.slice(end);
				element.value = newContent;
				form.setFieldValue(element.id, newContent);
			}
		} catch (error) {
			console.log(error);
		}
	}, [disableCopyPaste, token, sendPasteNotification, stringCopied, form]);

	useEffect(() => {
		const intervalId = setInterval(saveAttempt, 2000);

		return () => clearInterval(intervalId);
	}, [saveAttempt]);

	const handleMQFormSubmit = useCallback(async (values: MQFormValues) => {
		await onSubmit({
			isStepwise: true,
			stepwiseUserInput: JSON.stringify(Object.values(values)),
		});
	}, [onSubmit]);

	return (
		<div className="attempt-mq-container" style={{ padding: '1rem' }}>
			<div>
				<span className="info-text">Answer below:</span>
				<Divider className="answer-divider" />
				<div>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleMQFormSubmit}
						style={{
							'--border-top': 'none',
							'--border-bottom': 'none',
							'--border-inner': 'none',
						}}
					>
						{
							questionData.questionTypeMQ?.testCases?.map((testCase, index) => (
								<Form.Item
									key={testCase._id}
									name={index}
									label={(
										<div className="mq-details-container">
											<div className="mq-index">
												<span>{index + 1}</span>
											</div>
											<div className="mq-details">
												<pre style={{
													whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit',
												}}
												>
													{testCase.input}
												</pre>
											</div>
										</div>
									)}
									initialValue={(
										getAttempt()?.values?.[index]
										?? questionData.questionTypeMQ?.lastSubmitted?.[index]
									)}
								>
									<Input.TextArea
										allowClear
										autoSize={{ minRows: 3, maxRows: 5 }}
										disabled={isSubmitting}
										onCopy={(ev) => {
											if (disableCopyPaste) {
												ev.preventDefault();
												handleCopy(ev);
											}
										}}
										onPaste={(ev) => {
											if (disableCopyPaste) {
												ev.preventDefault();
												handlePaste(ev);
											}
										}}
									/>
								</Form.Item>
							))
						}
					</Form>
				</div>
			</div>
			{portalTarget ? createPortal(
				<Button
					type="button"
					size="large"
					onClick={() => form.submit()}
					loading={isSubmitting}
					color="primary"
					shape="rounded"
					style={{ padding: '6px 36px', fontSize: '16px', fontWeight: 600 }}
				>
					submit
				</Button>,
				portalTarget,
			) : (
				<div className="submit-mq-container">
					<Button
						type="button"
						size="large"
						onClick={() => form.submit()}
						loading={isSubmitting}
						color="primary"
						shape="rounded"
					>
						submit
					</Button>
				</div>
			)}
		</div>
	);
};
